import React, { useEffect, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cargoApi } from '../../services/api';
import { toast } from 'sonner';

// Record an official / third-party document as received: number, date and (optionally) the issuer's PDF.
export function ExternalDocDialog({ bookingId, requirement, onOpenChange, onSaved }) {
  const [reference, setReference] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const open = Boolean(requirement);

  useEffect(function () {
    if (requirement) {
      const ext = requirement.external || {};
      setReference(ext.reference || ''); setIssuedAt((ext.issued_at || '').slice(0, 10)); setFile(null);
    }
  }, [requirement]);

  async function save(e) {
    e.preventDefault();
    if (!reference.trim() && !file) { toast.error('Enter the document number or attach the file'); return; }
    setSaving(true);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append('file', file); fd.append('reference', reference.trim()); fd.append('issued_at', issuedAt);
        res = await cargoApi.upload(bookingId, requirement.key, fd);
      } else {
        res = await cargoApi.setReference(bookingId, requirement.key, { reference: reference.trim(), issued_at: issuedAt || null });
      }
      toast.success(requirement.title + ' recorded');
      onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record the document');
    } finally {
      setSaving(false);
    }
  }

  if (!requirement) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="external-doc-dialog">
        <DialogHeader><DialogTitle>{requirement.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600 -mt-2">Issued by <span className="text-ink">{requirement.issued_by}</span>. {requirement.description}</p>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label htmlFor="external-reference">Document / certificate no.</Label><Input id="external-reference" value={reference} onChange={function (e) { setReference(e.target.value); }} className="rounded-sm sv-mono" data-testid="external-reference" /></div>
            <div className="space-y-1"><Label htmlFor="external-issued-at">Date of issue</Label><Input id="external-issued-at" type="date" value={issuedAt} onChange={function (e) { setIssuedAt(e.target.value); }} className="rounded-sm" data-testid="external-issued-at" /></div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="external-file">Issuer's file (PDF / image, max 10 MB)</Label>
            <Input id="external-file" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={function (e) { setFile(e.target.files[0] || null); }} className="rounded-sm" data-testid="external-file" />
            {requirement.external?.filename && !file && <p className="text-xs text-gray-500">Current file: {requirement.external.filename}</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={function () { onOpenChange(false); }} data-testid="external-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-ink hover:bg-ink-800" data-testid="external-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Record as received</>}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
